import { Plus } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Card,
  ChipToggle,
  Dialog,
  EmptyState,
  ErrorState,
  Field,
  Input,
  LoadingState,
  Select,
} from '@/components/ui';
import type { Role } from '@/features/auth/roles';
import { useMe, useSession } from '@/features/auth/session-context';
import { useBranch } from '@/features/branch/branch-context';
import {
  useDirectory,
  useMessagingActions,
  useThread,
  useThreads,
} from '@/features/messaging/api';
import type { PatientSummary } from '@/features/patients/types';
import { PageContainer } from '@/layouts/PageContainer';
import { PatientPicker } from '@/pages/queue/PatientPicker';
import styles from './shared.module.css';
import { CLINIC_TZ } from '@/lib/clinic-time';

const time = (iso: string) =>
  new Date(iso).toLocaleTimeString('en-GB', {
    timeZone: CLINIC_TZ,
    hour: '2-digit',
    minute: '2-digit',
  });

/**
 * Team messages (SEC-09, #2): threads about patients between staff, so
 * hand-offs and photos stay in the clinic's records instead of personal
 * WhatsApp. Each thread is tied to a patient or a desk.
 */
export function TeamMessagesPage() {
  const { selected: branch } = useBranch();
  return <BranchMessages key={branch?.id ?? 'all'} />;
}
function BranchMessages() {
  const { t } = useTranslation();
  const { selected: branch } = useBranch();
  const threadQuery = useThreads();
  const { data: threads, isLoading, error } = threadQuery;
  const [openId, setOpenId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const activeId =
    threads?.find((t) => t.id === openId)?.id ?? threads?.[0]?.id ?? null;

  if (!branch)
    return (
      <PageContainer>
        <p className={styles.note}>{t('team.chooseBranch')}</p>
      </PageContainer>
    );

  return (
    <PageContainer>
      <p className={styles.notice}>{t('team.notice')}</p>
      <div className={styles.messagesGrid}>
        <Card
          title={t('team.threads')}
          actions={
            <Button
              size="sm"
              icon={<Plus size={14} />}
              onClick={() => setCreating(true)}
            >
              {t('team.new')}
            </Button>
          }
        >
          {isLoading ? (
            <LoadingState />
          ) : error ? (
            <ErrorState
              error={error}
              onRetry={() => void threadQuery.refetch()}
            />
          ) : !threads?.length ? (
            <EmptyState title={t('team.empty')} body={t('team.emptyBody')} />
          ) : (
            <ul className={styles.threads} aria-label={t('team.threads')}>
              {threads.map((t) => (
                <li key={t.id}>
                  <button
                    type="button"
                    className={[
                      styles.thread,
                      t.id === activeId && styles.threadActive,
                    ]
                      .filter(Boolean)
                      .join(' ')}
                    aria-current={t.id === activeId}
                    onClick={() => setOpenId(t.id)}
                  >
                    <span className={styles.threadTitle}>
                      {t.title}
                      {t.unread && (
                        <span className={styles.unread} aria-label="unread" />
                      )}
                    </span>
                    <span className={styles.threadSub}>{t.lastMessage}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </Card>
        {activeId && <ThreadPane key={activeId} id={activeId} />}
      </div>
      {creating && (
        <NewThreadDialog
          onClose={() => setCreating(false)}
          onCreated={(id) => {
            setCreating(false);
            setOpenId(id);
          }}
        />
      )}
    </PageContainer>
  );
}

function ThreadPane({ id }: { id: string }) {
  const { t: translate } = useTranslation();
  const me = useMe();
  const threadQuery = useThread(id);
  const { data: t } = threadQuery;
  const { send } = useMessagingActions();
  const [error, setError] = useState<unknown>();
  const [body, setBody] = useState('');
  if (threadQuery.error)
    return (
      <ErrorState
        error={threadQuery.error}
        onRetry={() => void threadQuery.refetch()}
      />
    );
  if (!t) return <LoadingState />;
  return (
    <Card
      title={t.title}
      actions={
        <span className={styles.participants}>
          {t.participants.map((p) => p.fullName).join(', ')}
        </span>
      }
    >
      <ol className={styles.messages} aria-label="Messages">
        {t.messages.map((m) => (
          <li
            key={m.id}
            className={m.author.id === me.id ? styles.mine : styles.theirs}
          >
            <span className={styles.byline}>
              {m.author.fullName} · {time(m.createdAt)}
            </span>
            <span>{m.body}</span>
          </li>
        ))}
      </ol>
      <form
        className={styles.composer}
        onSubmit={(e) => {
          e.preventDefault();
          const text = body.trim();
          if (!text) return;
          setError(undefined);
          void send
            .mutateAsync({ threadId: t.id, body: text })
            .then(() => setBody(''))
            .catch(setError);
        }}
      >
        <Input
          maxLength={4000}
          aria-label={translate('team.write')}
          placeholder={translate('team.write')}
          value={body}
          onChange={(e) => setBody(e.target.value)}
        />
        <Button
          type="submit"
          variant="primary"
          size="sm"
          disabled={!body.trim()}
          loading={send.isPending}
        >
          {translate('team.send')}
        </Button>
      </form>
      {error !== undefined && <ErrorState error={error} />}
    </Card>
  );
}

function NewThreadDialog({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (id: string) => void;
}) {
  const { t } = useTranslation();
  const { hasPermission } = useSession();
  const canPatient = hasPermission('patients:read');
  const [about, setAbout] = useState<'patient' | 'desk'>(
    canPatient ? 'patient' : 'desk',
  );
  const [patient, setPatient] = useState<PatientSummary | null>(null);
  const [desk, setDesk] = useState<Role | ''>('');
  const [people, setPeople] = useState<string[]>([]);
  const [body, setBody] = useState('');
  const directory = useDirectory(true);
  const { data: dir } = directory;
  const { create } = useMessagingActions();
  const [error, setError] = useState<unknown>();
  const ready =
    !!dir &&
    !directory.error &&
    (about === 'patient' ? !!patient : !!desk) &&
    body.trim().length > 0 &&
    body.length <= 4000;

  return (
    <Dialog
      open
      title={t('team.newThread')}
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          <Button
            variant="primary"
            disabled={!ready}
            loading={create.isPending}
            onClick={() =>
              create
                .mutateAsync({
                  ...(about === 'patient'
                    ? { patientId: patient!.id }
                    : { deskRole: desk as Role }),
                  participantIds: people,
                  body: body.trim(),
                })
                .then((t) => onCreated(t.id))
                .catch(setError)
            }
          >
            {t('team.start')}
          </Button>
        </>
      }
    >
      <div className={styles.form}>
        {error !== undefined && <ErrorState error={error} />}
        {directory.isLoading && <LoadingState />}
        {directory.error && (
          <ErrorState
            error={directory.error}
            onRetry={() => void directory.refetch()}
          />
        )}
        {canPatient && (
          <ChipToggle
            label={t('team.about')}
            single
            options={[
              { value: 'patient', label: t('team.patient') },
              { value: 'desk', label: t('team.desk') },
            ]}
            selected={[about]}
            onChange={(v) => v[0] && setAbout(v[0])}
          />
        )}
        {about === 'patient' ? (
          <PatientPicker value={patient} onChange={setPatient} />
        ) : (
          <Field label={t('team.deskLabel')}>
            <Select
              value={desk}
              placeholder={t('team.chooseDesk')}
              options={(dir?.desks ?? []).map((d) => ({
                value: d.key,
                label: d.name,
              }))}
              onChange={(e) => setDesk(e.target.value as Role)}
            />
          </Field>
        )}
        <ChipToggle
          label={t('team.people')}
          options={(dir?.staff ?? []).map((s) => ({
            value: s.id,
            label: `${s.fullName} · ${s.roles.join(', ')}`,
          }))}
          selected={people}
          onChange={setPeople}
        />
        <Field label={t('team.message')}>
          <textarea
            maxLength={4000}
            className={styles.textarea}
            rows={4}
            value={body}
            onChange={(e) => setBody(e.target.value)}
          />
        </Field>
      </div>
    </Dialog>
  );
}
